import { createTRPCClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "@panelgenie/api/router";
import { buildAuthHeaders, buildTrpcUrl } from "./config.js";

/**
 * Typed tRPC client factory for the staff support console.
 *
 * The factory is parameterized over a `getToken` function so the
 * caller (`App.tsx`) wires it to Clerk's `useAuth().getToken()`. The
 * link calls `getToken` per request, picking up the latest session JWT
 * without keeping the token in a module-level variable. `getToken` may
 * return `null` for anonymous traffic; the link converts `null` to
 * `undefined` so `buildAuthHeaders` sees the same "absent" shape it
 * does in unit tests.
 *
 * Returning the typed `createTRPCClient<AppRouter>` shape means the
 * routes can call `client.support.listInvocations.query({...})` /
 * `client.support.getInvocation.query({ id })` with full inference from
 * the shared `AppRouter`.
 */
export type TrpcClient = ReturnType<typeof createTRPCClient<AppRouter>>;

export type CreateTrpcClientOptions = {
  readonly apiBaseUrl: string;
  /**
   * Async function returning the current Clerk session token, or
   * `null` when the user is signed out. Mirrors Clerk's
   * `useAuth().getToken()` signature so the App-level wiring is a
   * direct pass-through.
   */
  readonly getToken: () => Promise<string | null>;
};

export const createTrpcClient = (
  options: CreateTrpcClientOptions
): TrpcClient => {
  return createTRPCClient<AppRouter>({
    links: [
      httpBatchLink({
        url: buildTrpcUrl(options.apiBaseUrl),
        headers: async () => {
          const raw = await options.getToken();
          const token = raw === null ? undefined : raw;
          return buildAuthHeaders(token);
        },
      }),
    ],
  });
};
